import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Mail, Brain, Pencil, CheckCircle, Layout } from "lucide-react";

const PROMPTS = [
  "7-day Christmas sale campaign for a skincare brand",
  "SaaS onboarding welcome sequence for trial users",
  "Abandoned cart recovery emails for an ecommerce store",
  "Black Friday countdown for an electronics store",
  "Re-engagement campaign for inactive subscribers",
];

const STEPS = [
  { icon: Brain, color: "text-purple-500", bg: "bg-purple-50", label: "Planning…" },
  { icon: Pencil, color: "text-blue-500", bg: "bg-blue-50", label: "Writing…" },
  { icon: Layout, color: "text-teal", bg: "bg-teal/10", label: "Templates…" },
  { icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-50", label: "Ready!" },
];

export default function AiCampaignGeneratorModal() {
  const [displayText, setDisplayText] = useState("");
  const [activeStep, setActiveStep] = useState(-1);
  const [phase, setPhase] = useState<"typing" | "processing" | "done" | "pause">("typing");
  const cancelRef = useRef(false);
  const promptIdx = useRef(0);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const t = setTimeout(resolve, ms);
        const check = setInterval(() => {
          if (cancelRef.current) {
            clearTimeout(t);
            clearInterval(check);
            resolve();
          }
        }, 50);
      }),
    [],
  );

  useEffect(() => {
    cancelRef.current = false;

    async function typeText(str: string) {
      for (let i = 0; i < str.length; i++) {
        if (cancelRef.current) return;
        setDisplayText((p) => p + str[i]);
        await sleep(38 + Math.random() * 22);
      }
    }

    async function clearText() {
      let len = 0;
      setDisplayText((p) => { len = p.length; return p; });
      await sleep(10);
      for (let i = 0; i < len; i++) {
        if (cancelRef.current) return;
        setDisplayText((p) => p.slice(0, -1));
        await sleep(14 + Math.random() * 6);
      }
    }

    async function run() {
      while (!cancelRef.current) {
        const prompt = PROMPTS[promptIdx.current % PROMPTS.length];
        promptIdx.current++;

        setPhase("typing");
        setActiveStep(-1);
        await typeText(prompt);
        await sleep(600);

        if (cancelRef.current) return;
        setPhase("processing");
        for (let i = 0; i < STEPS.length; i++) {
          if (cancelRef.current) return;
          setActiveStep(i);
          await sleep(i === STEPS.length - 1 ? 500 : 700);
        }

        setPhase("done");
        await sleep(2800);

        setPhase("pause");
        setActiveStep(-1);
        await clearText();
        await sleep(400);
      }
    }

    const timer = setTimeout(run, 800);
    return () => {
      cancelRef.current = true;
      clearTimeout(timer);
    };
  }, [sleep]);

  return (
    <Link
      to="/ai-campaign-generator"
      className="block w-full max-w-xl group"
    >
      <div className="relative bg-white rounded-2xl border border-black/[0.08] overflow-hidden hover:border-coral/30 hover:shadow-xl hover:shadow-coral/[0.06] transition-all duration-300 shadow-lg shadow-black/[0.04]">
        {/* Header bar */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-black/[0.02] border-b border-black/[0.06]">
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-400/70" />
              <div className="w-2 h-2 rounded-full bg-amber-400/70" />
              <div className="w-2 h-2 rounded-full bg-green-400/70" />
            </div>
            <div className="flex items-center gap-1.5 ml-1">
              <Sparkles size={11} className="text-coral" />
              <span className="text-[11px] font-semibold text-slate/70">AI Campaign Generator</span>
            </div>
          </div>
          <span className="text-[10px] font-bold text-coral bg-coral/[0.08] px-2.5 py-0.5 rounded-full uppercase tracking-wider group-hover:bg-coral/15 transition-colors">
            Try free
          </span>
        </div>

        {/* Prompt area */}
        <div className="px-4 py-3">
          <div className="flex items-start gap-2.5 min-h-[1.75rem]">
            <div className="w-5 h-5 rounded bg-coral/[0.08] flex items-center justify-center flex-shrink-0 mt-0.5">
              <Mail size={10} className="text-coral/60" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[13px] text-ink/80 font-medium leading-snug">
                {displayText}
              </span>
              {(phase === "typing" || phase === "pause") && (
                <span className="text-coral font-light animate-pulse ml-0.5">|</span>
              )}
            </div>
          </div>

          {/* Steps indicator */}
          {(phase === "processing" || phase === "done") && (
            <div className="flex items-center gap-1.5 mt-2.5">
              {STEPS.map((step, i) => {
                const visible = i <= activeStep;
                const Icon = step.icon;
                const isActive = i === activeStep && phase !== "done";
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-all duration-300 ${
                      visible
                        ? isActive
                          ? `${step.bg} ${step.color}`
                          : "bg-black/[0.03] text-slate/50"
                        : "opacity-0 scale-90"
                    }`}
                  >
                    <Icon size={10} className={visible ? (isActive ? step.color : "text-slate/40") : ""} />
                    <span className="hidden sm:inline whitespace-nowrap">{step.label}</span>
                  </div>
                );
              })}
              {phase === "done" && (
                <span className="ml-auto text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                  5 emails created
                </span>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-black/[0.04] bg-black/[0.01]">
          <span className="text-[10px] text-slate/40">
            One prompt → complete email campaign
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-coral group-hover:gap-1.5 transition-all">
            Generate <ArrowRight size={10} className="group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
}
